import React, { useCallback, useEffect, useRef, useState } from "react";

interface IframeCarouselProps {
  sources: { src: string; title: string }[];
  caption: string;
  size?: "mini" | "small" | "medium" | "large" | number;
}

const IframeCarousel: React.FC<IframeCarouselProps> = ({
  sources,
  caption,
  size = "large",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getWidth = () => {
    if (typeof window !== "undefined" && window.innerWidth <= 768)
      return "95vw";
    if (typeof size === "number") return `${size}%`;

    const map: Record<string, string> = {
      mini: "40%",
      small: "60%",
      medium: "75%",
      large: "100%",
    };

    return map[size] || "75%";
  };

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? sources.length - 1 : prev - 1));
  }, [sources.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === sources.length - 1 ? 0 : prev + 1));
  }, [sources.length]);

  return (
    <div ref={containerRef} style={styles.outerContainer}>
      <figure style={styles.figureContainer}>
        <div style={styles.controlsContainer}>
          <button onClick={goToPrev} style={styles.externalNavButton}>
            ‹
          </button>

          <div
            style={{
              ...styles.iframeWrapper,
              width: getWidth(),
              maxWidth: containerWidth,
            }}
          >
            <iframe
              style={styles.iframe}
              src={sources[currentIndex].src}
              allowFullScreen
              title={sources[currentIndex].title}
            ></iframe>
          </div>

          <button onClick={goToNext} style={styles.externalNavButton}>
            ›
          </button>
        </div>

        <figcaption style={styles.caption}>{caption}</figcaption>
      </figure>
    </div>
  );
};

const styles = {
  outerContainer: {
    width: "100%",
    padding: "0 10px",
    boxSizing: "border-box" as const,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  },
  figureContainer: {
    width: "100%",
    maxWidth: "1000px",
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  },
  controlsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    position: "relative" as const,
  },
  iframeWrapper: {
    flexGrow: 1,
    width: "100%",
    maxWidth: "100%",
  },
  iframe: {
    width: "100%",
    height: "450px",
    border: "1px solid rgba(0,0,0,0.1)",
  },
  caption: {
    fontSize: "14px",
    color: "#666",
    textAlign: "center" as const,
    paddingTop: "8px",
  },
  externalNavButton: {
    fontSize: "2rem",
    backgroundColor: "transparent",
    border: "none",
    color: "#333",
    cursor: "pointer",
    padding: "0 15px",
    zIndex: 10,
  },
};
export default IframeCarousel;
